import { useEffect, useState } from "react";
import { MailCheck } from "lucide-react";
import Button from "../Button/Button";
import "./EmailCodeStep.scss";

/**
 * Krok "zadajte kód z e-mailu".
 *
 * PRECO VLASTNY KOMPONENT (R7):
 *   Rovnaky krok potrebuju uz styri toky -- ziadost o prevadzku, registracia
 *   zakaznika, obnova hesla a zmena e-mailu v profile. Bez neho by sa
 *   sestmiestne pole, odpocet do znovuposlania aj texty o spame kopirovali
 *   dokola a kazda kopia by sa casom lisila.
 *
 * Komponent NEVIE, na co je kod: overenie aj znovuposlanie robi rodic cez
 * `onSubmit` a `onResend`. Drzi si len to, co patri obrazovke -- zadany kod
 * a odpocet.
 *
 * Props:
 *  - email       {string}  adresa, na ktoru kod odisiel (ukazuje sa cloveku)
 *  - onSubmit    {fn}      (kod: string) => Promise -- rodic overi a pokracuje
 *  - onResend    {fn}      () => Promise            -- posle kod znova
 *  - onBack      {fn}      spat na predosly krok (nepovinne)
 *  - submitLabel {string}  text hlavneho tlacidla
 *  - backLabel   {string}  text odkazu spat
 *  - popis       {node}    vlastne vysvetlenie namiesto predvoleneho
 *  - chyba       {string}  chybova hlaska od rodica
 *  - info        {string}  informacna hlaska od rodica
 *  - odosiela    {bool}    prebieha odosielanie
 *  - platnostMin {number}  ako dlho kod plati
 */
const DLZKA_KODU = 6;
const ODPOCET_S = 30;

function EmailCodeStep({
  email,
  onSubmit,
  onResend,
  onBack,
  submitLabel = "Overiť kód",
  backLabel = "Upraviť e-mail",
  popis,
  chyba,
  info,
  odosiela = false,
  platnostMin = 15,
}) {
  const [kod, setKod] = useState("");
  const [odpocet, setOdpocet] = useState(ODPOCET_S);

  // Odpocet do znovuposlania. Brzdi zbytocne maily aj vtedy, ked prvy este
  // len ide -- bez neho ludia klikaju "poslať znova" skor, nez mail dorazi.
  useEffect(() => {
    if (odpocet <= 0) return undefined;
    const t = setTimeout(() => setOdpocet((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [odpocet]);

  const odosli = (e) => {
    e.preventDefault();
    if (kod.length !== DLZKA_KODU || odosiela) return;
    onSubmit(kod);
  };

  const znova = async () => {
    if (odpocet > 0 || odosiela) return;
    setOdpocet(ODPOCET_S);
    setKod("");
    await onResend?.();
  };

  return (
    <div className="email-code">
      <MailCheck size={40} className="email-code__icon" aria-hidden="true" />
      <h2 className="email-code__title">Overte svoj e-mail</h2>

      <p className="email-code__text">
        {popis || (
          <>
            Na <strong>{email}</strong> sme poslali {DLZKA_KODU}-miestny overovací kód.
            Zadajte ho sem. Kód platí {platnostMin} minút.
          </>
        )}
      </p>

      <form onSubmit={odosli} className="email-code__form">
        <label className="email-code__field">
          <span>Overovací kód</span>
          <input
            className="email-code__input"
            value={kod}
            onChange={(e) => setKod(e.target.value.replace(/\D/g, "").slice(0, DLZKA_KODU))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            maxLength={DLZKA_KODU}
            aria-label="Overovací kód z e-mailu"
            autoFocus
          />
        </label>

        {chyba && <p className="email-code__error" role="alert">{chyba}</p>}
        {info && <p className="email-code__hint" aria-live="polite">{info}</p>}

        <Button type="submit" variant="primary" disabled={odosiela || kod.length !== DLZKA_KODU}>
          {odosiela ? "Overujem..." : submitLabel}
        </Button>
      </form>

      <div className="email-code__actions">
        {onResend && (
          <button type="button" className="email-code__link" onClick={znova} disabled={odosiela || odpocet > 0}>
            {odpocet > 0 ? `Poslať kód znova (${odpocet} s)` : "Poslať kód znova"}
          </button>
        )}
        {onBack && (
          <button type="button" className="email-code__link" onClick={onBack} disabled={odosiela}>
            {backLabel}
          </button>
        )}
      </div>

      <p className="email-code__hint">Kód neprišiel? Pozrite sa aj do priečinka so spamom.</p>
    </div>
  );
}

export default EmailCodeStep;
